import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

type Entity = { Entity: string };
type InputItem = {
  parentNode: string;
  Approver?: Entity[];
  Notifications: Entity[];
  isPresent: boolean;
};

type OutputNode = {
  key: string;
  label: string;
  data: string;
  children?: OutputNode[];
};


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatModuleList(allModules) {
  const moduleDetails = allModules.map(module => {
    return {
      value: module.ModuleId,
      label: module.ModuleName
    }
  });

  return moduleDetails;
}

export function formatModulePagesList(allModulePages) {
  const modulePages = allModulePages.map(modPage => {
    return {
      id: modPage.WorkflowId,
      name: modPage.WorkflowName
    }
  })
}

export function formatWorkflowTree_Test(selectedWorkflow, input, selectedNodesString = "") {
  const rootKey = "0";

  // Helper to convert an array of entities to OutputNode children with keys
  function mapEntitiesToChildren(
    entities: Entity[],
    parentKey: string
  ): OutputNode[] {
    return entities.map((entity, idx) => ({
      key: `${parentKey}-${idx}`,
      label: entity.Entity,
      data: entity.Entity,
    }));
  }

  // Convert each input item to OutputNode with children
  const childrenNodes: OutputNode[] = input.map((item, idx) => {
    const parentKey = `${rootKey}-${idx}`;

    const children: OutputNode[] = [];

    // Add Notifications children if present
    if (item.Notifications && item.Notifications.length > 0) {
      children.push({
        key: `${parentKey}-0`,
        label: "Notifications",
        data: "Notifications",
        children: mapEntitiesToChildren(item.Notifications, `${parentKey}-0`),
      });
    }

    // Add Approver children if present
    if (item.Approver && item.Approver.length > 0) {
      children.push({
        key: `${parentKey}-1`,
        label: "Approver",
        data: "Approver",
        children: mapEntitiesToChildren(item.Approver, `${parentKey}-1`),
      });
    }

    return {
      key: parentKey,
      label: item.parentNode,
      data: item.parentNode,
      children,
    };
  });

  // Root node wrapping all children
  const rootNode: OutputNode = {
    key: rootKey,
    label: selectedWorkflow,
    data: selectedWorkflow,
    children: childrenNodes,
  };

  return [rootNode];
  // let workflow = getSelectedWorkflow(allWorkflow, selectedWorkflow);
}

export function formatWorkflowTree_older(selectedWorkflow, input, selectedNodesString = "") {
  const rootKey = "0";

  const selectedPaths = selectedNodesString
    .split(",")
    .map(path => path.split(">"));

  function isPathSelected(currentPath) {
    return selectedPaths.some(selectedPath => {
      if (selectedPath.length !== currentPath.length) return false;
      return selectedPath.every((label, idx) => label === currentPath[idx]);
    });
  }

  // Recursive function to build nodes and compute checked/partialChecked
  function buildNode(label, data, key, currentPath, children = []) {
    // Determine checked/partialChecked for children first
    const childrenCheckedStates = children.map(child => ({
      checked: !!child.checked,
      partialChecked: !!child.partialChecked,
    }));

    const allChildrenChecked = children.length > 0 && childrenCheckedStates.every(c => c.checked);
    const someChildrenChecked = childrenCheckedStates.some(c => c.checked || c.partialChecked);

    // Check if this node itself is selected
    const selfChecked = isPathSelected(currentPath);

    // Determine node state
    let checked = false;
    let partialChecked = false;

    if (selfChecked) {
      // If node itself selected, it is fully checked
      checked = true;
    } else if (allChildrenChecked) {
      // All children checked → node checked
      checked = true;
    } else if (someChildrenChecked) {
      // Some children checked → partialChecked
      partialChecked = true;
    }

    // Return node object with appropriate flags
    const node = {
      key,
      label,
      data,
    };
    if (children.length > 0) {
      node.children = children;
    }
    if (checked) {
      node.checked = true;
    } else if (partialChecked) {
      node.partialChecked = true;
    }

    return node;
  }

  function mapEntitiesToChildren(entities, parentKey, currentPath) {
    return entities.map((entity, idx) => {
      const key = `${parentKey}-${idx}`;
      const newPath = [...currentPath, entity.Entity];
      // Leaf nodes have no children
      return buildNode(entity.Entity, entity.Entity, key, newPath, []);
    });
  }

  function mapInputItem(item, idx) {
    const parentKey = `${rootKey}-${idx}`;
    const currentPath = [item.parentNode];

    const children = [];

    // Notifications
    if (item.Notifications && item.Notifications.length > 0) {
      const notificationsPath = [...currentPath, "NOTIFICATIONS"];
      const notificationsChildren = mapEntitiesToChildren(item.Notifications, `${parentKey}-0`, notificationsPath);

      const notificationsNode = buildNode(
        "NOTIFICATIONS",
        "NOTIFICATIONS",
        `${parentKey}-0`,
        notificationsPath,
        notificationsChildren
      );
      children.push(notificationsNode);
    }

    // Approver
    if (item.Approver && item.Approver.length > 0) {
      const approverPath = [...currentPath, "APPROVER"];
      const approverChildren = mapEntitiesToChildren(item.Approver, `${parentKey}-1`, approverPath);

      const approverNode = buildNode(
        "APPROVER",
        "APPROVER",
        `${parentKey}-1`,
        approverPath,
        approverChildren
      );
      children.push(approverNode);
    }

    // Parent node with children
    return buildNode(item.parentNode, item.parentNode, parentKey, currentPath, children);
  }

  const childrenNodes = input.map(mapInputItem);

  const rootNode = buildNode(selectedWorkflow, selectedWorkflow, rootKey, [selectedWorkflow], childrenNodes);

  return [rootNode];
}


export function formatWorkflowTree(selectedWorkflow, workflowApprovalLevels) {
  const root = {
    id: selectedWorkflow,
    label: selectedWorkflow,
    children: []
  };

  workflowApprovalLevels && workflowApprovalLevels?.forEach(level => {
    const parentId = level.parentNode;
    const parentNode = {
      id: parentId,
      label: parentId,
      children: []
    };

    // APPROVERS section
    if (level.Approver && level.Approver.length > 0) {
      const approversNode = {
        id: `${parentId}>APPROVERS`,
        label: 'APPROVERS',
        children: []
      };

      level.Approver.forEach(approver => {
        approversNode.children.push({
          id: `${parentId}>APPROVERS>${approver.Entity}`,
          label: approver.Entity
        });
      });

      parentNode.children.push(approversNode);
    }

    // NOTIFICATIONS section
    // According to your example, NOTIFICATIONS node is always present even if empty,
    // but children only if entities exist.
    const notificationsNode = {
      id: `${parentId}>NOTIFICATIONS`,
      label: 'NOTIFICATIONS',
      children: []
    };

    if (level.Notifications && level.Notifications.length > 0) {
      level.Notifications.forEach(notification => {
        notificationsNode.children.push({
          id: `${parentId}>NOTIFICATIONS>${notification.Entity}`,
          label: notification.Entity
        });
      });
    }

    parentNode.children.push(notificationsNode);

    root.children.push(parentNode);
  });

  return [root];
}



function checkIfChecked(parentNode, children, selectedNodes) {
  let selectedNodesArray = selectedNodes.split(",");

}


// Helper function to find path by key in the tree
export function findPath(tree, targetKey, path = [], isRoot = true) {
  for (const node of tree) {
    // Skip root label ("My Details") in path as per example
    const currentPath = isRoot ? [] : [...path, node.label];
    if (node.key === targetKey) {
      return currentPath;
    }
    if (node.children) {
      const result = findPath(node.children, targetKey, currentPath, false);
      if (result) return result;
    }
  }
  return null;
}

export function getSelectedPaths(tree, selected) {
  const paths = [];

  for (const [key, status] of Object.entries(selected)) {
    if (status.checked || status.partialChecked) {
      const pathLabels = findPath(tree, key);
      if (pathLabels) {
        // Convert "Notifications" to uppercase as per example output
        const formattedPath = pathLabels.map(label =>
          label.toLowerCase() === "notifications" ? "NOTIFICATIONS" : label
        );
        paths.push(formattedPath.join(">"));
      }
    }
  }

  // Remove duplicates and sort
  const uniquePaths = [...new Set(paths)].sort();

  return uniquePaths.join(",");
}

export function validateAllFields(templateDetails, requireWorkflowId) {
  const {name, description, module, subject, subjectFields, type, fields, createdBy, employer, active, wfNodes, wfId, templateHtml} = templateDetails;
  let errorMessages = [];
  console.log("wfId")
  console.log(wfId)
  if (module === undefined || module === "" || module.trim() === "") {
    errorMessages.push("Module is mandatory field");
  }

  if (type === undefined || type === "" || type === 0) {
    errorMessages.push("Approval & Notification Type is mandatory field");
  }

  if (requireWorkflowId && (wfId === undefined || wfId === "" || wfId === 0)) {
    errorMessages.push("Workflow is mandatory field");
  }

  if (name === undefined || name === "" || name.trim() === "") {
    errorMessages.push("Name is mandatory field");
  }

  if (description === undefined || description === "" || description.trim() === "") {
    errorMessages.push("Description is mandatory field");
  }

  if (subject === undefined || subject === "" || subject.trim() === "") {
    errorMessages.push("Subject is mandatory field");
  }

  if (
    typeof templateHtml !== 'string' ||
    templateHtml.trim() === '' ||
    isHtmlVisuallyEmpty(templateHtml)
  ) {
    errorMessages.push("Please add Template body");
  }

  return errorMessages;
}

function isHtmlVisuallyEmpty(html) {
  return html
    .replace(/<[^>]*>/g, '')                      // remove HTML tags
    .replace(/[\u200B-\u200D\uFEFF]/g, '')        // remove zero-width spaces
    .trim() === '';
}

/*
type Entity = { Entity: string };
type InputItem = {
  parentNode: string;
  Approver?: Entity[];
  Notifications: Entity[];
  isPresent: boolean;
};

type OutputNode = {
  key: string;
  label: string;
  data: string;
  children?: OutputNode[];
};

const inputArray: InputItem[] = [
  {
    parentNode: "Level1",
    Approver: [
      { Entity: "HR" },
      { Entity: "CEOOOOOO" },
      { Entity: "CEO" },
    ],
    Notifications: [
      { Entity: "HR" },
      { Entity: "Initiator" },
    ],
    isPresent: true,
  },
  {
    parentNode: "NOTIFY APPROVAL",
    Notifications: [
      { Entity: "HR" },
      { Entity: "Initiator" },
    ],
    isPresent: true,
  },
  {
    parentNode: "NOTIFY REJECTION",
    Notifications: [
      { Entity: "HR" },
      { Entity: "Initiator" },
    ],
    isPresent: true,
  },
  {
    parentNode: "NOTIFY PULLBACK",
    Notifications: [
      { Entity: "HR" },
      { Entity: "Initiator" },
    ],
    isPresent: true,
  },
];

function convertInputToOutput(input: InputItem[]): OutputNode[] {
  const rootKey = "0";

  // Helper to convert an array of entities to OutputNode children with keys
  function mapEntitiesToChildren(
    entities: Entity[],
    parentKey: string
  ): OutputNode[] {
    return entities.map((entity, idx) => ({
      key: `${parentKey}-${idx}`,
      label: entity.Entity,
      data: entity.Entity,
    }));
  }

  // Convert each input item to OutputNode with children
  const childrenNodes: OutputNode[] = input.map((item, idx) => {
    const parentKey = `${rootKey}-${idx}`;

    const children: OutputNode[] = [];

    // Add Notifications children if present
    if (item.Notifications && item.Notifications.length > 0) {
      children.push({
        key: `${parentKey}-0`,
        label: "Notifications",
        data: "Notifications",
        children: mapEntitiesToChildren(item.Notifications, `${parentKey}-0`),
      });
    }

    // Add Approver children if present
    if (item.Approver && item.Approver.length > 0) {
      children.push({
        key: `${parentKey}-1`,
        label: "Approver",
        data: "Approver",
        children: mapEntitiesToChildren(item.Approver, `${parentKey}-1`),
      });
    }

    return {
      key: parentKey,
      label: item.parentNode,
      data: item.parentNode,
      children,
    };
  });

  // Root node wrapping all children
  const rootNode: OutputNode = {
    key: rootKey,
    label: "My Details",
    data: "Details",
    children: childrenNodes,
  };

  return [rootNode];
}

const outputArray = convertInputToOutput(inputArray);
console.log(JSON.stringify(outputArray, null, 2));

*/